import moment from "moment";
import { localize } from "src/l10n";
import { SpintrTypes } from "src/typings";
import { fromNow } from "src/utils";

const getStrFromOpenTime = (openTime) => {
    if (!openTime.isOpen) {
        return localize("Stangd");
    }

    return localize("OPEN") +
        " " +
        moment(openTime.startTime).format("HH:mm") +
        " - " +
        moment(openTime.endTime).format("HH:mm"); 
}

export const getOpenTimes = (openTimes: Spintr.IOpenTimeDay[]) => {
    let result = [];

    for (const openTime of openTimes) {
        for (const day of openTime.days) {
            result.push({
                ...openTime,
                dayOfWeek: day
            });
        }
    }

    result = result.sort(function (a, b) {
        if (a.dayOfWeek < b.dayOfWeek) {
            return -1;
        }
        if (a.dayOfWeek > b.dayOfWeek) {
            return 1;
        }
        return 0;
    });

    return result;
}

const getOpenTime = (openTimes) => {
    if (!openTimes || openTimes.length === 0) {
        return "";
    }

    const now = new Date();

    const dayOfWeek = now.getDay();

    for (const openTime of openTimes) {
        if (openTime.days.indexOf(dayOfWeek) > -1) {
            return getStrFromOpenTime(openTime);
        }
    }

    return localize("Stangd");
}

export const systemStatusTypeToUserState = (systemStatusType: SpintrTypes.SystemStatusType) => {
    switch (systemStatusType) {
        case 1:
            return 4;
        case 2:
            return 1;
        case 3:
            return 2;
        default:
            return 0;
    }
}

export const formatDates = (startDate: Date, endDate: Date) => {
    const start = moment(startDate);
    const format = "lll";
    const startString = start.format(format);

    if (!endDate) {
        return startString;
    }

    const end = moment(endDate);

    if (end.isSame(start)) {
        return startString;
    }

    const endString = start.isSame(end, "day") ? end.format("LT") : end.format(format);

    return startString + " - " + endString; 
}

export const icons: string[] = [
    "bar",
    "building",
    "cafe",
    "car",
    "fuel_station",
    "gym_equipment",
    "gym_machine",
    "gym",
    "lift",
    "network",
    "parking",
    "pool",
    "restaurant",
    "safety",
    "service",
    "ski",
    "software",
    "store",
    "taxi",
    "tractor",
    "trailer",
    "vehicle",
    "vending_machine",
];

export type SystemStatusBannerType = "clearBanner" | "ongoing" | "planned" | "clear" |"default";

export const isOpenRightNow = (openTimes: Spintr.IOpenTimeDay[]): boolean => {
    if (openTimes === null || openTimes.length === 0) {
        return false;
    }

    const now = moment();
    const rule = openTimes.find(x => x.days.indexOf(now.isoWeekday()) > -1);

    if (!rule) {
        return false;
    }

    const timeToday = t => now.clone().hour(moment(t).hour()).minute(moment(t).minute()).second(0);

    const isOpenNow = rule.isOpen &&
        rule.days.includes(now.isoWeekday()) &&
        now.isBetween(timeToday(rule.startTime), timeToday(rule.endTime), null, '[)');

    return isOpenNow;
}

export const formatSeasonDisplay = (seasons?: Spintr.ISystemStatusSeason[]): string => {
    if (!seasons || seasons.length === 0) {
        return localize("Alla");
    }

    if (seasons.length === 1) {
        return seasons[0].name;
    }

    return `${seasons[0].name} +${seasons.length - 1}`;
};

const doneRecencyMs = 24 * 60 * 60 * 1000;

export type SystemStatusListCellState = {
    textTag: string;
    relativeTime?: string;
    isClickable: boolean;
    postId?: number;
};

type ResourceStatusEntry = {
    status: SpintrTypes.SystemStatusType;
    endDate?: Date | string;
    postId?: number;
};

function toValidDate(value?: Date | string): Date | undefined {
    if (!value) {
        return undefined;
    }

    const date = value instanceof Date ? value : new Date(value);
    return Number.isNaN(date.getTime()) ? undefined : date;
}

function getLatestDoneStatus(resource: Spintr.ISystemStatusResource): ResourceStatusEntry | undefined {
    const doneStatuses = (resource.activeSystemStatuses || [])
        .filter((status: ResourceStatusEntry) => status?.status === SpintrTypes.SystemStatusType.Done)
        .slice()
        .sort((left: ResourceStatusEntry, right: ResourceStatusEntry) => {
            const leftDate = toValidDate(left?.endDate);
            const rightDate = toValidDate(right?.endDate);

            if (!leftDate && !rightDate) {
                return 0;
            }

            if (!leftDate) {
                return 1;
            }

            if (!rightDate) {
                return -1;
            }

            return rightDate.getTime() - leftDate.getTime();
        });

    return doneStatuses[0];
}

export function getSystemStatusListCellState(resource: Spintr.ISystemStatusResource): SystemStatusListCellState {
    const statuses = (resource.activeSystemStatuses || []) as ResourceStatusEntry[];
    const hasOngoing = statuses.some((status) => status?.status === SpintrTypes.SystemStatusType.Ongoing);
    const hasPlanned = statuses.some((status) => status?.status === SpintrTypes.SystemStatusType.Planned);

    if (hasOngoing) {
        return {
            textTag: "Pagaende",
            isClickable: false,
        };
    }

    if (hasPlanned) {
        return {
            textTag: "Planerad",
            isClickable: false,
        };
    }

    const latestDone = getLatestDoneStatus(resource);

    if (!latestDone) {
        return {
            textTag: "NO_PLANNED_UPDATES",
            isClickable: false,
        };
    }

    const endDate = toValidDate(latestDone.endDate);
    const isRecentDone = !endDate || Date.now() - endDate.getTime() <= doneRecencyMs;

    if (!isRecentDone) {
        return {
            textTag: "NO_PLANNED_UPDATES",
            isClickable: false,
        };
    }

    if (!endDate) {
        return {
            textTag: "Avklarad",
            isClickable: !!latestDone.postId,
            postId: latestDone.postId,
        };
    }

    return {
        textTag: "Avklarad",
        relativeTime: fromNow(endDate),
        isClickable: !!latestDone.postId,
        postId: latestDone.postId,
    };
}

export default getOpenTime;
